import { deleteJson, getJson, postJson } from './client';
import type { SpringPageResponse } from './community';

/**
 * 고객지원 콘텐츠 + 1:1 문의 (supportservice).
 *
 * 콘텐츠 조회는 익명 허용. audience 는 호출 앱 구분 — 웹(유저 앱)은 'USER' 고정,
 * 미지정 시 공용(ALL) 콘텐츠만 내려온다.
 */

// --- 지원 콘텐츠 (공지/이벤트/팁/FAQ) ---

export type SupportArticleType = 'notices' | 'events' | 'tips' | 'faqs';

export type SupportArticleDto = {
  articleId: string;
  articleType: string;
  audience: string;
  category: string | null;
  title: string;
  /** 목록 응답에서는 null (summary), 상세에서만 본문 포함. */
  content: string | null;
  thumbnailUrl: string | null;
  linkUrl: string | null;
  pinned: boolean;
  ongoing: boolean;
  publishedAt: string | null;
  startsAt: string | null;
  endsAt: string | null;
  viewCount: number;
  createdAt: string;
  updatedAt: string;
};

export function fetchSupportArticles(
  type: SupportArticleType,
  params: { category?: string; page?: number; size?: number } = {},
) {
  const query = new URLSearchParams({ audience: 'USER' });
  if (params.category) query.set('category', params.category);
  if (params.page != null) query.set('page', String(params.page));
  if (params.size != null) query.set('size', String(params.size));
  return getJson<SpringPageResponse<SupportArticleDto>>(
    `/api/v1/support/${type}?${query.toString()}`,
    { preserveAuthOnUnauthorized: true },
  );
}

export function fetchSupportArticleCategories(type: SupportArticleType) {
  return getJson<string[]>(`/api/v1/support/${type}/categories?audience=USER`, {
    preserveAuthOnUnauthorized: true,
  });
}

export function fetchSupportArticleDetail(type: SupportArticleType, articleId: string) {
  return getJson<SupportArticleDto>(
    `/api/v1/support/${type}/${encodeURIComponent(articleId)}?audience=USER`,
    { preserveAuthOnUnauthorized: true },
  );
}

// --- 1:1 문의 (로그인 필수 — /api/v1/users/me/**) ---

export type SupportInquiryStatus = string;

export type SupportInquiryAnswerDto = {
  answerId: string;
  adminUserId: string;
  content: string;
  createdAt: string;
  updatedAt: string;
};

export type SupportInquiryDto = {
  inquiryId: string;
  userId: string;
  audience: string;
  category: string | null;
  title: string;
  content: string;
  contactEmail: string | null;
  status: SupportInquiryStatus;
  imageUrls: string[];
  answers: SupportInquiryAnswerDto[];
  answerCount: number;
  answeredAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type CreateSupportInquiryRequest = {
  category?: string;
  title: string;
  content: string;
  contactEmail?: string;
  imageUrls?: string[];
};

export function createSupportInquiry(req: CreateSupportInquiryRequest) {
  return postJson<SupportInquiryDto>('/api/v1/users/me/inquiries', {
    audience: 'USER',
    ...req,
  });
}

export function fetchMySupportInquiries(params: { page?: number; size?: number } = {}) {
  const query = new URLSearchParams();
  if (params.page != null) query.set('page', String(params.page));
  if (params.size != null) query.set('size', String(params.size));
  const qs = query.toString();
  return getJson<SpringPageResponse<SupportInquiryDto>>(
    `/api/v1/users/me/inquiries${qs ? `?${qs}` : ''}`,
  );
}

export function fetchMySupportInquiry(inquiryId: string) {
  return getJson<SupportInquiryDto>(`/api/v1/users/me/inquiries/${encodeURIComponent(inquiryId)}`);
}

export async function withdrawSupportInquiry(inquiryId: string) {
  await deleteJson<Record<string, string>>(
    `/api/v1/users/me/inquiries/${encodeURIComponent(inquiryId)}`,
  );
}
